import React, { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import LoaderSuspense from "../components/common/LoaderSuspense";
import { useGetCatalogVendeurQuery } from "../utils/api/catalog/catalog.api";
import {
  applyCatalogShareMetaFromVendeur,
  resetCatalogShareMeta,
} from "../utils/catalogMeta";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import {
  getCartCount,
  getTotalAmount,
  removeCart,
  setCartItems,
} from "../redux/slice/useCartSlice";
import CatalogShareFab from "./CatalogShareFab";
import {
  clearCatalogCart,
  loadCatalogCart,
  saveCatalogCart,
} from "./catalogCartStorage";
import { setCatalogTenant } from "./catalogTenant";

export default function VendeurCatalogLayout() {
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector((s) => s.cart.cartItems);
  const { shopSlug } = useParams<{ shopSlug: string }>();
  const location = useLocation();
  const slug = shopSlug?.trim() ?? "";

  useEffect(() => {
    if (slug) {
      setCatalogTenant(slug, null);
    }
  }, [slug]);

  const { data, isLoading, isError } = useGetCatalogVendeurQuery(slug, {
    skip: !slug,
  });

  const catalogKey = data?.catalog_slug || slug;
  const cartHydratedRef = useRef(false);
  const skipNextSaveRef = useRef(false);

  useEffect(() => {
    cartHydratedRef.current = false;
    skipNextSaveRef.current = true;
  }, [catalogKey]);

  useEffect(() => {
    if (!slug || !data) return;
    setCatalogTenant(catalogKey, data);
  }, [slug, data, catalogKey]);

  useEffect(() => {
    if (!data) return;
    applyCatalogShareMetaFromVendeur(data);
    return () => {
      resetCatalogShareMeta();
    };
  }, [data]);

  /** Restaure le panier catalogue une seule fois à l'entrée (pas après une suppression). */
  useEffect(() => {
    if (!catalogKey || cartHydratedRef.current) return;

    const saved = loadCatalogCart(catalogKey);
    if (saved.length > 0) {
      dispatch(setCartItems(saved));
    } else {
      dispatch(removeCart());
    }
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    cartHydratedRef.current = true;
  }, [catalogKey, dispatch]);

  /** Sauvegarde le panier ; vide le stockage session quand le panier est vide. */
  useEffect(() => {
    if (!catalogKey || !cartHydratedRef.current) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (cartItems.length === 0) {
      clearCatalogCart(catalogKey);
      return;
    }
    saveCatalogCart(catalogKey, cartItems);
  }, [catalogKey, cartItems]);

  const renderContent = () => {
    if (!slug) {
      return <Navigate to="/site-non-disponible" replace />;
    }

    if (isLoading) {
      return <LoaderSuspense />;
    }

    if (isError || !data) {
      return <Navigate to="/site-non-disponible" replace />;
    }

    if (!data.is_active) {
      return <Navigate to="/site-non-disponible" replace />;
    }

    if (data.catalog_slug && slug !== data.catalog_slug) {
      const suffix =
        location.pathname.replace(/^\/catalogue\/[^/]+/, "") || "";
      return (
        <Navigate
          to={`/catalogue/${data.catalog_slug}${suffix}${location.search}`}
          replace
        />
      );
    }

    return <Outlet />;
  };

  const showShareFab = !!data?.is_active && !isLoading;

  return (
    <>
      {renderContent()}
      {showShareFab && <CatalogShareFab />}
    </>
  );
}
